import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';


import PermitHeader from './components/PermitHeader';
import WorkDescription from './components/WorkDescription';
import PersonnelSection from './components/PersonnelSection';
import HazardAssessment from './components/HazardAssessment';
import StatusTimeline from './components/StatusTimeline';
import ExtensionManagement from './components/ExtensionManagement';
import AuditTrail from './components/AuditTrail';
import CommentsSection from './components/CommentsSection';
import DocumentAttachments from './components/DocumentAttachments';
import Button from '../../components/ui/Button';
import Icon from '../../components/ui/Icon';

const WorkPermitDetailsContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const permitData = {
    permitNumber: "WP-2025-0847",
    workType: "Hot Work - Welding Operations",
    status: "Active",
    priority: "High",
    hazardLevel: "High",
    location: "Plant 1 - Main Facility",
    area: "Production Floor - Section B",
    startDate: "12/03/2025 08:00 AM",
    endDate: "12/03/2025 06:00 PM",
    issuedBy: "Sarah Mitchell",
    issueDate: "12/02/2025 03:30 PM"
  };

  const workDescription = {
    description: "Perform welding repairs on damaged conveyor belt support structure in Production Floor Section B. Work involves cutting and welding of steel support beams to restore structural integrity. Area requires hot work permit due to welding operations near combustible materials. All safety protocols for hot work must be strictly followed including fire watch, ventilation, and spark containment measures.",
    equipment: [
    "Arc Welding Machine (300A)",
    "Angle Grinder with Cut-off Wheels",
    "Fire Extinguishers (CO2 Type)",
    "Welding Screens and Barriers",
    "Personal Protective Equipment",
    "Gas Detection Equipment"],

    procedures: [
    "Conduct pre-work safety briefing with all personnel involved in the operation",
    "Establish 15-meter hot work zone with appropriate barriers and warning signage",
    "Remove all combustible materials from work area or protect with fire-resistant covers",
    "Ensure adequate ventilation and continuous air quality monitoring throughout operation",
    "Maintain fire watch for minimum 60 minutes after completion of all hot work activities"]

  };

  const personnel = [
  {
    id: 1,
    name: "Michael Rodriguez",
    role: "Supervisor",
    employeeId: "EMP-2847",
    contact: "+1 (555) 234-5678",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d29504f2-1763294463041.png",
    avatarAlt: "Professional headshot of Hispanic male supervisor with short black hair wearing navy blue work uniform and safety vest",
    certified: true
  },
  {
    id: 2,
    name: "Jennifer Chen",
    role: "Safety Officer",
    employeeId: "EMP-1923",
    contact: "+1 (555) 345-6789",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb9cc40e-1763295859243.png",
    avatarAlt: "Professional headshot of Asian female safety officer with long black hair wearing white hard hat and orange safety vest",
    certified: true
  },
  {
    id: 3,
    name: "David Thompson",
    role: "Technician",
    employeeId: "EMP-3156",
    contact: "+1 (555) 456-7890",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_177c205bb-1763300910211.png",
    avatarAlt: "Professional headshot of Caucasian male technician with brown hair wearing blue work coveralls and yellow hard hat",
    certified: true
  },
  {
    id: 4,
    name: "Maria Santos",
    role: "Worker",
    employeeId: "EMP-4289",
    contact: "+1 (555) 567-8901",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1612278f8-1763294059882.png",
    avatarAlt: "Professional headshot of Hispanic female worker with curly brown hair wearing green safety uniform and protective eyewear",
    certified: false
  }];


  const hazards = [
  {
    id: 1,
    type: "Fire",
    severity: "Critical",
    description: "High risk of fire ignition due to welding sparks and hot metal particles in proximity to combustible materials and flammable substances",
    controls: [
    "Establish 15-meter fire-free zone with all combustibles removed or protected",
    "Position two fire extinguishers within immediate reach of work area",
    "Assign dedicated fire watch personnel for duration of work plus 60 minutes",
    "Conduct hot work area inspection before, during, and after operations"]

  },
  {
    id: 2,
    type: "Electrical",
    severity: "High",
    description: "Risk of electric shock from welding equipment and potential contact with live electrical systems in the work area",
    controls: [
    "Verify all welding equipment is properly grounded and inspected",
    "Ensure GFCI protection on all electrical connections",
    "Maintain dry work environment and use insulated tools",
    "Lock out and tag out nearby electrical systems as required"]

  },
  {
    id: 3,
    type: "Chemical",
    severity: "Medium",
    description: "Exposure to welding fumes, metal oxides, and potentially toxic gases during cutting and welding operations",
    controls: [
    "Provide adequate local exhaust ventilation at welding point",
    "Use respiratory protection equipment rated for welding fumes",
    "Monitor air quality continuously with gas detection equipment",
    "Ensure proper material safety data sheets available for all materials"]

  }];


  const timeline = [
  {
    id: 1,
    status: "Created",
    description: "Work permit created and submitted for approval by operations supervisor",
    timestamp: "12/02/2025 02:15 PM",
    performedBy: "Michael Rodriguez",
    role: "Operations Supervisor"
  },
  {
    id: 2,
    status: "Approved",
    description: "Permit reviewed and approved by safety officer after hazard assessment verification",
    timestamp: "12/02/2025 03:30 PM",
    performedBy: "Jennifer Chen",
    role: "Safety Officer"
  },
  {
    id: 3,
    status: "Active",
    description: "Work permit activated and operations commenced with all safety measures in place",
    timestamp: "12/03/2025 08:00 AM",
    performedBy: "Michael Rodriguez",
    role: "Operations Supervisor"
  }];


  const [extensions, setExtensions] = useState([
  {
    id: 1,
    reason: "Additional structural repairs required",
    justification: "During initial welding work, additional structural damage was discovered that requires immediate attention to maintain facility safety. Extension needed to complete comprehensive repairs.",
    duration: "4 Hours",
    status: "Approved",
    requestedBy: "Michael Rodriguez",
    requestDate: "12/03/2025 02:30 PM",
    approvedBy: "Jennifer Chen",
    approvalDate: "12/03/2025 02:45 PM"
  }]
  );

  const activities = [
  {
    id: 1,
    type: "Created",
    user: "Michael Rodriguez",
    action: "created the work permit",
    timestamp: "12/02/2025 02:15 PM",
    details: "Initial permit creation for hot work operations"
  },
  {
    id: 2,
    type: "Updated",
    user: "Michael Rodriguez",
    action: "updated permit details",
    timestamp: "12/02/2025 02:45 PM",
    details: "Added additional safety requirements and personnel assignments",
    changes: ["Added fire watch requirement", "Updated equipment list"]
  },
  {
    id: 3,
    type: "Approved",
    user: "Jennifer Chen",
    action: "approved the permit",
    timestamp: "12/02/2025 03:30 PM",
    details: "Permit approved after safety review and hazard assessment"
  },
  {
    id: 4,
    type: "Attachment",
    user: "Jennifer Chen",
    action: "uploaded safety documentation",
    timestamp: "12/02/2025 03:45 PM",
    details: "Uploaded hot work safety checklist and area inspection report"
  },
  {
    id: 5,
    type: "Commented",
    user: "David Thompson",
    action: "added a comment",
    timestamp: "12/03/2025 07:45 AM",
    details: "Confirmed all equipment inspected and ready for operations"
  },
  {
    id: 6,
    type: "Extended",
    user: "Michael Rodriguez",
    action: "requested permit extension",
    timestamp: "12/03/2025 02:30 PM",
    details: "Requested 4-hour extension due to additional repairs needed"
  }];


  const [comments, setComments] = useState([
  {
    id: 1,
    author: "Jennifer Chen",
    role: "Safety Officer",
    content: "All safety equipment has been verified and is in proper working condition. Fire extinguishers are positioned at required locations. Please ensure fire watch maintains continuous monitoring throughout the operation.",
    timestamp: "12/02/2025 04:00 PM",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb9cc40e-1763295859243.png",
    avatarAlt: "Professional headshot of Asian female safety officer with long black hair wearing white hard hat and orange safety vest"
  },
  {
    id: 2,
    author: "David Thompson",
    role: "Technician",
    content: "Pre-work inspection completed. All welding equipment tested and functioning properly. Work area has been cleared of combustible materials and barriers are in place.",
    timestamp: "12/03/2025 07:45 AM",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_177c205bb-1763300910211.png",
    avatarAlt: "Professional headshot of Caucasian male technician with brown hair wearing blue work coveralls and yellow hard hat",
    attachments: ["pre-work-inspection.pdf"]
  },
  {
    id: 3,
    author: "Michael Rodriguez",
    role: "Supervisor",
    content: "Work is progressing well. Additional structural damage discovered requires extension. All safety protocols being maintained. Fire watch is active and monitoring continuously.",
    timestamp: "12/03/2025 02:15 PM",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d29504f2-1763294463041.png",
    avatarAlt: "Professional headshot of Hispanic male supervisor with short black hair wearing navy blue work uniform and safety vest"
  }]
  );

  const [documents, setDocuments] = useState([
  {
    id: 1,
    name: "Hot Work Safety Checklist.pdf",
    type: "pdf",
    size: "245 KB",
    uploadedBy: "Jennifer Chen",
    uploadDate: "12/02/2025 03:45 PM",
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b223179f-1764663164148.png",
    alt: "Document preview showing hot work safety checklist with multiple checkboxes and safety requirements listed in organized sections"
  },
  {
    id: 2,
    name: "Work Area Inspection Report.pdf",
    type: "pdf",
    size: "312 KB",
    uploadedBy: "Jennifer Chen",
    uploadDate: "12/02/2025 03:50 PM",
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1b223179f-1764663164148.png",
    alt: "Document preview displaying work area inspection report with detailed findings and safety compliance notes"
  },
  {
    id: 3,
    name: "Equipment Inspection Photo.jpg",
    type: "image",
    size: "1.8 MB",
    uploadedBy: "David Thompson",
    uploadDate: "12/03/2025 07:30 AM",
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1773703be-1764695268680.png",
    alt: "Industrial welding equipment setup showing arc welding machine with cables and safety equipment arranged on factory floor"
  },
  {
    id: 4,
    name: "Fire Safety Equipment.jpg",
    type: "image",
    size: "2.1 MB",
    uploadedBy: "David Thompson",
    uploadDate: "12/03/2025 07:35 AM",
    url: "https://images.unsplash.com/photo-1604656329788-e66ad1dfe01b",
    alt: "Two red fire extinguishers positioned near welding work area with safety signage visible in industrial setting"
  }]
  );

  const handleRequestExtension = (formData) => {
    const newExtension = {
      id: extensions?.length + 1,
      reason: formData?.reason,
      justification: formData?.justification,
      duration: `${formData?.duration} Hours`,
      status: "Pending",
      requestedBy: "Michael Rodriguez",
      requestDate: new Date()?.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
    setExtensions([...extensions, newExtension]);
  };

  const handleAddComment = (content) => {
    const newComment = {
      id: comments?.length + 1,
      author: "Current User",
      role: "Operations Supervisor",
      content: content,
      timestamp: new Date()?.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_154ec26e0-1763295122488.png",
      avatarAlt: "Professional headshot of male operations supervisor with short brown hair wearing blue work uniform and safety glasses"
    };
    setComments([...comments, newComment]);
  };

  const handleUploadDocuments = (files) => {
    const newDocs = Array.from(files)?.map((file, index) => ({
      id: documents?.length + index + 1,
      name: file?.name,
      type: file?.type?.includes('image') ? 'image' : 'pdf',
      size: `${(file?.size / 1024)?.toFixed(0)} KB`,
      uploadedBy: "Current User",
      uploadDate: new Date()?.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }),
      url: "https://img.rocket.new/generatedImages/rocket_gen_img_144bb7c9d-1764797354442.png",
      alt: "Newly uploaded document preview showing safety documentation or work area photograph"
    }));
    setDocuments([...documents, ...newDocs]);
  };

  return (
    <div className={`main-content bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="text-foreground" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">{permitData?.permitNumber}</h1>
                <p className="text-muted-foreground mt-1">Work permit details and status</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Home"
                onClick={() => navigate('/')}
              >
                Return to Dashboard
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                onClick={() => console.log('Export permit')}
              >
                Export Permit
              </Button>
            </div>
          </div>
        </div>

        <main className="main-content">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-6">
              <PermitHeader permit={permitData} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <WorkDescription
                    description={workDescription?.description}
                    equipment={workDescription?.equipment}
                    procedures={workDescription?.procedures} />

                  
                  <HazardAssessment hazards={hazards} />
                  
                  <ExtensionManagement
                    extensions={extensions}
                    onRequestExtension={handleRequestExtension} />

                  
                  <CommentsSection
                    comments={comments}
                    onAddComment={handleAddComment} />

                  
                  <DocumentAttachments
                    documents={documents}
                    onUpload={handleUploadDocuments} />

                </div>

                <div className="space-y-6">
                  <PersonnelSection personnel={personnel} />
                  <StatusTimeline timeline={timeline} />
                  <AuditTrail activities={activities} />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default WorkPermitDetailsContent;